/* One scheduler and one observer service all category visuals. */
(() => {
  if (customElements.get('sega-category-motion')) return;
  const members = new Set();
  const images = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let timer;
  let paused = false;
  const load = (url) => {
    if (!images.has(url)) images.set(url, new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url;
    }));
    return images.get(url);
  };
  const tick = () => {
    timer = undefined;
    const now = performance.now();
    let active = false;
    members.forEach((member) => {
      if (!member.running()) return;
      member.advance(now);
      if (member.dataset.kind === 'athlete' || member.frames.length > 1) active = true;
    });
    if (active) timer = setTimeout(tick, 180);
  };
  const schedule = () => {
    clearTimeout(timer);
    timer = undefined;
    members.forEach((member) => member.sync());
    if ([...members].some((member) => member.running())) tick();
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => { entry.target.inViewport = entry.isIntersecting; });
    schedule();
  }, { threshold: .05 }) : null;
  class CategoryMotion extends HTMLElement {
    connectedCallback() {
      this.frames = [...this.querySelectorAll('[data-motion-frame]')];
      this.atlas = this.querySelector('.segastore-category-motion__atlas');
      this.inViewport = !observer;
      this.index = 0;
      this.lastFrame = 0;
      this.seed = [...(this.dataset.variant || this.dataset.category || '')].reduce((n, c) => n + c.charCodeAt(0), 0) % 6;
      this.ancestors = [];
      for (let parent = this.parentElement; parent; parent = parent.parentElement) {
        if (parent.tagName === 'DETAILS') this.ancestors.push(parent);
      }
      this.onToggle = () => schedule();
      this.ancestors.forEach((detail) => detail.addEventListener('toggle', this.onToggle));
      members.add(this);
      observer?.observe(this);
      schedule();
    }
    disconnectedCallback() {
      members.delete(this);
      observer?.unobserve(this);
      this.ancestors?.forEach((detail) => detail.removeEventListener('toggle', this.onToggle));
      schedule();
    }
    visible() {
      return this.isConnected && this.inViewport && this.ancestors.every((detail) => detail.open || detail.querySelector(':scope > summary')?.contains(this)) && !this.closest('[hidden]');
    }
    running() { return this.visible() && !document.hidden && !reduced.matches && !paused && !this.failed; }
    sync() {
      this.dataset.active = String(this.running());
      if (this.visible() && this.dataset.sheet && !this.loading && !this.ready && !this.failed) {
        this.loading = true;
        const sheets = [this.dataset.sheet, this.dataset.alternateSheet].filter(Boolean);
        Promise.all(sheets.map((url) => load(url).then(() => url).catch(() => null))).then((loaded) => {
          if (!this.isConnected) return;
          this.sheets = loaded.filter(Boolean);
          if (!this.sheets.length) { this.failed = true; this.dataset.active = 'false'; return; }
          this.atlas.style.backgroundImage = `url(${JSON.stringify(this.sheets[0])})`;
          this.ready = true;
          this.dataset.ready = 'true';
          schedule();
        }).catch(() => { this.failed = true; this.dataset.active = 'false'; });
      }
      if (this.visible() && this.frames.length) this.prepareFrame(this.index);
      if (reduced.matches && this.index !== 0) {
        this.index = 0;
        this.frames.forEach((frame, i) => frame.classList.toggle('is-current', i === 0));
      }
    }
    prepareFrame(index) {
      const frame = this.frames[index];
      if (!frame || frame.src || !frame.dataset.src) return;
      frame.src = frame.dataset.src;
    }
    advance(now) {
      if (this.ready && this.atlas) {
        const sheetIndex = Math.floor(now / 4800) % this.sheets.length;
        const sheet = this.sheets[sheetIndex];
        if (this.currentSheet !== sheet) {
          this.atlas.style.backgroundImage = `url(${JSON.stringify(sheet)})`;
          this.currentSheet = sheet;
          this.dataset.sheetIndex = String(sheetIndex);
        }
        const frameDuration = Number(this.dataset.frameDuration) || 240;
        const frame = (Math.floor(now / frameDuration) + this.seed) % 6;
        this.atlas.style.backgroundPosition = `${(frame % 3) * 50}% ${frame < 3 ? 0 : 100}%`;
        this.dataset.frame = String(frame);
      }
      if (this.frames.length < 2) return;
      const next = (this.index + 1) % this.frames.length;
      this.prepareFrame(next);
      if (now - this.lastFrame < 2400 || !this.frames[next].complete || !this.frames[next].naturalWidth) return;
      this.frames.forEach((frame, i) => frame.classList.toggle('is-current', i === next));
      this.index = next;
      this.dataset.frame = String(next);
      this.lastFrame = now;
    }
  }
  customElements.define('sega-category-motion', CategoryMotion);
  document.addEventListener('visibilitychange', schedule);
  reduced.addEventListener('change', schedule);
  const controls = () => {
    const buttons = document.querySelectorAll('[data-sport-mark-toggle]');
    const readPause = () => { paused = [...buttons].some((button) => button.getAttribute('aria-pressed') === 'true'); schedule(); };
    const controlObserver = new MutationObserver(readPause);
    buttons.forEach((button) => controlObserver.observe(button, { attributes: true, attributeFilter: ['aria-pressed'] }));
    readPause();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', controls, { once: true });
  else controls();
})();

